/**
 * Platform billing / revenue API wrapper (super-admin only).
 *
 * Backs `GET /api/admin/usage` → `getPlatformOverview`. Unlike the tenant credits
 * boundary (`credits.ts`, credits-only), this is the OPERATOR surface: real money
 * — COGS, revenue, profit — IS present here, alongside the credit-wallet picture.
 * The route is guarded by the super-admin check on the backend; this wrapper does
 * not soften that.
 *
 * The types below mirror the Elysia response schema 1:1, so the Eden-inferred
 * result is assignable to them with no cast. `name` is joined in the admin
 * controller (organization display name); the metering report itself stays a
 * money ledger. Two DISTINCT time semantics live on
 * every row and on the totals — the caller MUST label them so the operator isn't
 * misled:
 *   - Money (`platformCostUsd`, `byokNotionalUsd`, `billableUsd`, `marginUsd`,
 *     `marginPct`, `callCount`) is DATE-WINDOWED by the `from`/`to` query.
 *   - Credits (`creditsGranted/Purchased/Spent/Forfeited`, `creditBalance`,
 *     `creditBalanceUsd`) are LIFETIME/cumulative — a balance is a snapshot.
 */

import { api } from "./client.ts"
import { unwrapData } from "./errors.ts"

/** One tenant's cross-tenant money + credit row (super-admin). */
export interface IPlatformTenantRow {
  tenantId: string
  /** Organization display name. Null when the org row is gone. */
  name: string | null
  /** COGS — what the platform paid third-party providers (period-windowed). */
  platformCostUsd: number
  /** Notional cost of BYOK usage the customer paid for directly (period-windowed). */
  byokNotionalUsd: number
  /** Credit revenue — what the tenant is charged (period-windowed). */
  billableUsd: number
  /** Profit — billable − platform cost (period-windowed). */
  marginUsd: number
  /** Profit as a percentage of revenue (period-windowed). */
  marginPct: number
  /** Metered provider calls in the window (period-windowed). */
  callCount: number
  /** Σ grant-lot face credits (lifetime). */
  creditsGranted: number
  /** Σ purchase-lot face credits (lifetime). */
  creditsPurchased: number
  /** Platform spend converted to credits at the peg (lifetime). */
  creditsSpent: number
  /** Lapsed-lot forfeiture converted to credits at the peg (lifetime). */
  creditsForfeited: number
  /** Outstanding wallet balance in credits (lifetime snapshot). */
  creditBalance: number
  /** Money value of the outstanding credits — deferred liability (lifetime snapshot). */
  creditBalanceUsd: number
}

/** One provider's rolled-up cost (period-windowed). */
export interface IPlatformProviderRow {
  category: string
  provider: string
  mode: string
  costUsd: number
  callCount: number
}

/** Platform-wide totals — money windowed, credits cumulative. */
export interface IPlatformOverviewTotals {
  platformCostUsd: number
  byokNotionalUsd: number
  billableUsd: number
  marginUsd: number
  creditsGranted: number
  creditsPurchased: number
  creditsSpent: number
  creditsForfeited: number
  creditBalance: number
  creditBalanceUsd: number
}

/** The full super-admin billing overview. */
export interface IPlatformOverview {
  totals: IPlatformOverviewTotals
  perTenant: IPlatformTenantRow[]
  byProvider: IPlatformProviderRow[]
}

/**
 * Fetch the platform billing overview for a date window. `from`/`to` are inclusive
 * local-calendar `YYYY-MM-DD` bounds that scope the MONEY figures only — credit
 * fields are always lifetime/cumulative regardless of the window.
 */
export async function fetchPlatformBilling(from?: string, to?: string): Promise<IPlatformOverview> {
  return unwrapData(await api.api.admin.usage.get({ query: { from, to } }))
}
